var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import { Box, Chip, IconButton, Link, Tooltip } from '@mui/material';
import { formatNumber } from '../util/NumberUtil';
import { displayDate, getCurrentYearMonth, isAfterEndOfMonth, isPreviousYear, isValidYearMonth } from '../util/TimeUtil';
import { getDashboard, isXpmConnected, setDashboardNote, syncDashboardEntries, updateDashboardEntries } from '../rest/BackendApi';
import { useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import styles from '../style/common.module.css';
import dayjs from 'dayjs';
import TransactionsTable from '../components/TransactionsTable';
import MarkAsPendingIcon from '@mui/icons-material/PendingActions';
import MarkAsCompleteIcon from '@mui/icons-material/AssignmentTurnedIn';
import MailIcon from '@mui/icons-material/Mail';
import SyncIcon from '@mui/icons-material/Sync';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import AddNoteIcon from '@mui/icons-material/NoteAddOutlined';
import { DashboardStatusEnum } from '../model/DashboardStatusEnum';
import { PeriodEnum } from '../model/PeriodEnum';
import { customFilterWithPrefix, getMuiTheme } from '../components/Datatable';
import Loading from '../components/Loading';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import NoOrganisation from '../components/NoOrganisation';
import { SectionTitle, StyledMuiLink, SubText } from '../components/StyledComponent';
import OrgSyncStatusDisplay from '../components/SyncStatus';
import { BasisEnum } from '../model/BasisEnum';
import { humanize } from '../util/StringUtil';
import UserContext from '../UserContext';
import SupportLink from '../components/SupportLink';
import { getDatatableColumnConfig, getDatatableConfig, getItemFromLocalStorage, setItemInLocalStorage } from '../util/LocalStorageUtil';
import XeroLink from '../components/XeroLink';
import EmailSendingDialog from '../components/EmailSendingDialog';
import { AddTextField } from '../components/AddTextField';
import { AutoEllipsis } from '../components/AutoEllipsis';
import { getFullName } from '../util/DataUtil';
import MessageContext, { empty, error, success } from '../MessageContext';
import { hasFeature } from '../business/Subscription';
import JobsTable from '../components/JobsTableDialog';
import { GenericDialog } from '../components/GenericDialog';
import { DisplayWithSeeMoreChip } from '../components/DisplayWithSeeMoreChip';
export default function DashboardPage() {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const [dashboards, setDashboards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState(undefined);
    const [yearMonth, setYearMonth] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogContent, setDialogContent] = useState({ open: false });
    const [xpmConnected, setXpmConnected] = useState(false);
    const [emailDialogContent, setEmailDialogContent] = useState();
    const userContext = useContext(UserContext);
    const messageContext = useContext(MessageContext);
    const user = userContext === null || userContext === void 0 ? void 0 : userContext.user;
    const shouldShowXpm = xpmConnected;
    const shouldShowOpenAccess = hasFeature(user, 'MultiUser') && ((_a = user === null || user === void 0 ? void 0 : user.firm) === null || _a === void 0 ? void 0 : _a.openAccess);
    useEffect(() => {
        if (hasFeature(user, 'XPM')) {
            console.log('Checking xpm connected', user);
            isXpmConnected(false)
                .then(response => setXpmConnected(response.connected));
        }
    }, [user]);
    useEffect(() => {
        const monthValue = searchParams.get('month');
        if (isValidYearMonth(monthValue)) {
            setYearMonth(monthValue);
        }
        else {
            const storedYearMonth = getItemFromLocalStorage('dashboardYearMonth');
            if (storedYearMonth && isValidYearMonth(storedYearMonth)) {
                setYearMonth(storedYearMonth);
            }
            else {
                setYearMonth(getCurrentYearMonth());
            }
        }
    }, [searchParams]);
    useEffect(() => {
        if (yearMonth && isValidYearMonth(yearMonth)) {
            fetchData().then(() => {
                setSearchParams({ month: yearMonth });
                setItemInLocalStorage('dashboardYearMonth', yearMonth);
            });
        }
    }, [yearMonth]);
    const successStyle = { backgroundColor: 'rgba(26, 255, 0, 0.35)' };
    const fetchData = (afterSync) => __awaiter(this, void 0, void 0, function* () {
        try {
            setLoadingMessage(afterSync ? 'Initialising dashboard, this may take a few minutes' : 'Loading dashboard');
            setLoading(true);
            yield refreshDataBackground();
        }
        finally {
            setLoading(false);
            setLoadingMessage(undefined);
        }
    });
    const refreshDataBackground = () => __awaiter(this, void 0, void 0, function* () {
        const dashboards = yield getDashboard(yearMonth);
        if (dashboards) {
            setDashboards(dashboards);
        }
    });
    const onSyncReady = (syncStatus, attempt) => {
        if (syncStatus == null) {
            return;
        }
        // If attempt was greater than 1 or attempt 1 completed less than few seconds ago we refresh
        let syncCompletionTimeInMs = dayjs().diff(dayjs.utc(syncStatus.updateDate));
        if (attempt > 1 || syncCompletionTimeInMs < 5000) {
            fetchData(true);
        }
    };
    function markEntriesAs(selected, setSelected, status) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let updatedEntries = yield updateDashboardEntries(selected.map(row => row.id), status);
                const mappedEntry = new Map(updatedEntries.map(element => [element.id, element]));
                setDashboards(dashboards.map(entry => mappedEntry.get(entry.id) || entry));
                messageContext.setMessage(success(`Successfully marked entries as "${humanize(status)}"`));
                setSelected([]);
            }
            catch (exception) {
                messageContext.setMessage(error(_jsxs("span", { children: ["Couldn't mark as ", status, ". Please try again later or ", _jsx(SupportLink, {})] })));
            }
        });
    }
    function markAsComplete(selected, setSelected) {
        return __awaiter(this, void 0, void 0, function* () {
            return markEntriesAs(selected, setSelected, DashboardStatusEnum.COMPLETE);
        });
    }
    function markAsPending(selected, setSelected) {
        return __awaiter(this, void 0, void 0, function* () {
            return markEntriesAs(selected, setSelected, DashboardStatusEnum.PENDING);
        });
    }
    function syncWithXero(selected, setSelected) {
        return __awaiter(this, void 0, void 0, function* () {
            setLoadingMessage('Syncing dashboard for ' + selected.length + ' organisation' + (selected.length > 1 ? 's' : ''));
            setLoading(true);
            messageContext.setMessage(empty());
            try {
                const updatedEntries = yield syncDashboardEntries(selected.map(row => row.id));
                const mappedEntry = new Map(updatedEntries.map(element => [element.id, element]));
                setDashboards(dashboards.map(entry => mappedEntry.get(entry.id) || entry));
                setSelected([]);
                messageContext.setMessage(success('Dashboard entries synced with xero successfully'));
            }
            catch (exception) {
                messageContext.setMessage(error('Failed to sync with Xero. Log out and log back in if the problem persists.'));
            }
            finally {
                setLoading(false);
                setLoadingMessage(undefined);
            }
        });
    }
    function updateDashboardEntryAfterPostApiCall(updatedDashboard) {
        setDashboards(dashboards.map(d => (d.id === updatedDashboard.id ? updatedDashboard : d)));
    }
    function getStatusOrder(status) {
        switch (status) {
            case 'PENDING':
                return 2;
            case 'READY_TO_START':
                return 1;
            case 'COMPLETE':
                return 3;
            case 'NOT_REPORTING':
                return 4;
        }
        return 0;
    }
    function showDetailUnreconciledTransactions(transactions, total, shortCode, event) {
        setDialogContent({
            content: _jsx(TransactionsTable, { transactions: transactions, total: total, shortCode: shortCode }),
            title: 'Unreconciled Transactions',
            open: true,
            onClose: () => setDialogContent({ open: false }),
        });
        event.preventDefault();
        event.stopPropagation();
    }
    const numberBodyRender = (value) => {
        return formatNumber(value);
    };
    const zeroOrOtherFilterOptions = {
        names: ['$0.00', 'Other'],
        logic: (location, filters, row) => {
            let value = 'Other';
            if (location == '$0.00') {
                value = '$0.00';
            }
            if (filters.length)
                return !filters.includes(value);
            return false;
        },
    };
    const transactionsFilterOptions = {
        names: ['0', '1-5', '6-10', '10+'],
        logic: (body, filters, row) => {
            let value = '0';
            if (body > 10) {
                value = '10+';
            }
            else if (body > 5) {
                value = '6-10';
            }
            else if (body > 0) {
                value = '1-5';
            }
            if (filters.length)
                return !filters.includes(value);
            return false;
        },
    };
    const columns = [
        {
            name: 'xpmJobs',
            options: {
                display: 'excluded',
                filter: false,
                download: false,
            },
        },
        {
            name: 'organisation.name',
            label: 'Name',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex) => {
                    const row = dashboards[dataIndex];
                    return (_jsxs(_Fragment, { children: [_jsx("div", { style: { display: 'flex', alignItems: 'center', minWidth: '250px' }, children: _jsxs("div", { children: [_jsx(AutoEllipsis, { text: row.organisation.name, maxLength: 39 }), " ", _jsx(XeroLink, { shortCode: row.organisation.shortCode })] }) }), _jsxs(SubText, { children: ["Synced ", dayjs.utc(row.lastSynced).fromNow(), row.lastReminderSent && ' - Last email ' + dayjs.utc(row.lastReminderSent).fromNow()] })] }));
                },
            },
        },
        shouldShowOpenAccess && {
            name: 'organisation.connectedToCurrentUser',
            label: 'Organisation connected by',
            options: Object.assign(Object.assign({ filter: true }, customFilterWithPrefix('Connected by')), { display: 'excluded', customBodyRender: (data) => {
                    return data ? 'You' : 'Firm';
                } }),
        },
        {
            name: 'organisation.manager',
            label: 'Client Manager',
            options: Object.assign(Object.assign({ display: ((_b = getDatatableColumnConfig('dashboardConfig', 'organisation.manager')) === null || _b === void 0 ? void 0 : _b.display) || 'false', filter: true, download: false, filterType: 'multiselect', filterOptions: {
                    names: [...new Set(dashboards.map(dash => (dash.organisation.manager ? getFullName(dash.organisation.manager) : 'None')))].sort(),
                    logic: (body, filters, row) => {
                        if (body == null) {
                            return !filters.includes('None');
                        }
                        if (filters.length)
                            return !filters.includes(body);
                        return false;
                    },
                } }, customFilterWithPrefix('Manager')), { sort: true, customBodyRender: (manager) => {
                    return manager && getFullName(manager);
                } }),
        },
        {
            name: 'organisation.tags',
            label: 'Organisation Tags',
            options: Object.assign(Object.assign({ display: ((_c = getDatatableColumnConfig('dashboardConfig', 'organisation.tags')) === null || _c === void 0 ? void 0 : _c.display) || 'false', sort: false, filter: true, download: false, filterType: 'multiselect', filterOptions: {
                    names: [...new Set(dashboards.flatMap(dash => dash.organisation.tags.map(t => t.name)))].sort(),
                    logic: (tags, filters, row) => {
                        const tagNames = tags.map(t => t.name);
                        return !filters.some((filter) => tagNames.includes(filter));
                    },
                } }, customFilterWithPrefix('Tag')), { setCellProps: () => {
                    return { align: 'center' };
                }, customBodyRenderLite(dataIndex) {
                    const tags = dashboards[dataIndex].organisation.tags;
                    return (_jsx("div", { className: styles.centered, style: { gap: 2 }, children: tags.map(t => (_jsx(Chip, { variant: "outlined", size: "small", label: t.name }))) }));
                } }),
        },
        {
            name: 'organisation.basPeriod',
            label: 'Reporting',
            options: {
                filter: true,
                filterOptions: {
                    names: [PeriodEnum.MONTHLY, PeriodEnum.QUARTERLY],
                    logic(period, filterVal) {
                        return !filterVal.includes(PeriodEnum[period]);
                    },
                },
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRenderLite: (dataIndex) => {
                    const org = dashboards[dataIndex].organisation;
                    return (_jsx("div", { style: { display: 'flex', flexDirection: 'column', alignItems: 'center' }, children: _jsx(Tooltip, { title: _jsxs("span", { children: ["GST: ", PeriodEnum[org.gstPeriod], _jsx("br", {}), " PAYGW: ", PeriodEnum[org.paygwPeriod]] }), children: _jsx("span", { children: PeriodEnum[org.basPeriod] }) }) }));
                },
            },
        },
        {
            name: 'organisation.gstBasis',
            label: 'GST Basis',
            options: {
                filterOptions: {
                    names: [BasisEnum.ACCRUAL, BasisEnum.CASH],
                    logic(basis, filterVal) {
                        return !filterVal.includes(basis);
                    },
                },
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender: (value) => {
                    return BasisEnum[value];
                },
            },
        },
        {
            name: 'organisation.periodLockDate',
            label: 'User Lock Date',
            options: {
                display: ((_d = getDatatableColumnConfig('dashboardConfig', 'organisation.periodLockDate')) === null || _d === void 0 ? void 0 : _d.display) || 'false',
                filter: false,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender: (value) => {
                    return value ? displayDate(value) : '';
                },
            },
        },
        {
            name: 'organisation.endOfYearLockDate',
            label: 'Adviser Lock Date',
            options: {
                display: ((_e = getDatatableColumnConfig('dashboardConfig', 'organisation.endOfYearLockDate')) === null || _e === void 0 ? void 0 : _e.display) || 'false',
                filter: false,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender: (value) => {
                    return value ? displayDate(value) : '';
                },
            },
        },
        {
            name: 'nbUnreconciledBankTransactions',
            label: 'Unreconciled Bank Transactions',
            options: Object.assign(Object.assign({ filter: true, sort: true }, customFilterWithPrefix('Unreconciled Bank Transactions')), { filterOptions: Object.assign({}, transactionsFilterOptions), setCellHeaderProps: () => {
                    return { style: { maxWidth: '145px' } };
                }, setCellProps: (value, dataIndex) => {
                    const style = dashboards[dataIndex].nbUnreconciledBankTransactions === 0 && successStyle;
                    return { align: 'center', style: Object.assign({}, style) };
                }, customBodyRenderLite: (dataIndex) => {
                    let entry = dashboards[dataIndex];
                    const value = entry.nbUnreconciledBankTransactions;
                    return value > 0 ? (_jsx(Link, { component: "button", onClick: e => showDetailUnreconciledTransactions(entry.unreconciledBankTransactions, value, entry.organisation.shortCode, e), children: value })) : (0);
                } }),
        },
        {
            name: 'nbUnreconciledAccountTransactions',
            label: 'Unreconciled Account Transactions',
            options: Object.assign(Object.assign({ filter: true, sort: true }, customFilterWithPrefix('Unreconciled Account Transactions')), { filterOptions: Object.assign({}, transactionsFilterOptions), setCellHeaderProps: () => {
                    return { style: { maxWidth: '145px' } };
                }, setCellProps: (value, dataIndex) => {
                    const style = dashboards[dataIndex].nbUnreconciledAccountTransactions === 0 && successStyle;
                    return { align: 'center', style: Object.assign({}, style) };
                }, customBodyRenderLite: (dataIndex) => {
                    let entry = dashboards[dataIndex];
                    const value = entry.nbUnreconciledAccountTransactions;
                    return value > 0 ? (_jsx(Link, { component: "button", onClick: e => showDetailUnreconciledTransactions(entry.unreconciledAccountTransactions, value, entry.organisation.shortCode, e), children: value })) : (0);
                } }),
        },
        {
            name: 'paygwBas',
            label: 'PAYGW BAS',
            options: Object.assign(Object.assign({}, customFilterWithPrefix('PAYGW BAS')), { filterOptions: Object.assign({}, zeroOrOtherFilterOptions), customBodyRender: numberBodyRender, setCellProps: (value, dataIndex) => {
                    const style = dashboards[dataIndex].paygwBas === dashboards[dataIndex].paygwAccount && successStyle;
                    return { align: 'right', style: Object.assign(Object.assign({}, style), { paddingRight: '10px' }) };
                } }),
        },
        {
            name: 'paygwAccount',
            label: 'PAYGW Account',
            options: Object.assign(Object.assign({}, customFilterWithPrefix('PAYGW Account')), { filterOptions: Object.assign({}, zeroOrOtherFilterOptions), customBodyRender: numberBodyRender, setCellProps: (value, dataIndex) => {
                    const style = dashboards[dataIndex].paygwBas === dashboards[dataIndex].paygwAccount && successStyle;
                    return { align: 'right', style: Object.assign(Object.assign({}, style), { paddingRight: '10px' }) };
                } }),
        },
        {
            name: 'accountPayable',
            label: 'Wages Payable',
            options: Object.assign(Object.assign({}, customFilterWithPrefix('Wages Payable')), { filterOptions: Object.assign({}, zeroOrOtherFilterOptions), customBodyRender: numberBodyRender, setCellProps: (value, dataIndex) => {
                    const style = dashboards[dataIndex].accountPayable === 0 && successStyle;
                    return { align: 'right', style: Object.assign(Object.assign({}, style), { paddingRight: '10px' }) };
                } }),
        },
        {
            name: 'super',
            label: 'Super Payable/Accrued',
            options: {
                sort: false,
                filter: false,
                setCellProps: (value, dataIndex) => {
                    const style = dashboards[dataIndex].superPayable <= dashboards[dataIndex].superAccrued && successStyle;
                    return { align: 'center', style: Object.assign({}, style) };
                },
                customBodyRenderLite: (dataIndex) => {
                    const row = dashboards[dataIndex];
                    return formatNumber(row.superPayable) + ' / ' + formatNumber(row.superAccrued);
                },
            },
        },
        {
            name: 'lastPayrunPosted',
            label: 'Last Payrun Posted',
            options: {
                filterOptions: {
                    names: ['Payrun before period Date', 'Payrun after period Date', 'No payrun posted'],
                    logic(status, filterVal) {
                        if (status == null || status == '') {
                            return !filterVal.includes('No payrun posted');
                        }
                        const value = isAfterEndOfMonth(yearMonth, status) ? 'Payrun after period date' : 'Payrun before period date';
                        return !filterVal.includes(value);
                    },
                },
                setCellProps: (value, dataIndex) => {
                    const lastPayrunPosted = dashboards[dataIndex].lastPayrunPosted;
                    const style = isAfterEndOfMonth(yearMonth, lastPayrunPosted) && successStyle;
                    return { align: 'center', style: Object.assign({}, style) };
                },
                customBodyRender: (value) => {
                    return value ? displayDate(value) : '';
                },
            },
        },
        {
            name: 'gstBalance',
            label: 'GST Balance',
            options: Object.assign(Object.assign({}, customFilterWithPrefix('GST Balance')), { filterOptions: Object.assign({}, zeroOrOtherFilterOptions), customBodyRender: numberBodyRender, setCellProps: () => {
                    return { align: 'right', style: { paddingRight: '10px' } };
                } }),
        },
        {
            name: 'note',
            label: 'Note',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const row = dashboards[dataIndex];
                    return (_jsx("div", { style: { width: '100%' }, children: _jsx(AddTextField, { hoverText: row.note, allowEmpty: true, addIcon: row.note ? _jsx(EditNoteIcon, { sx: { color: 'orange' } }) : _jsx(AddNoteIcon, { color: "action" }), rows: 4, editMinWidth: "300px", textEditLabel: "Note", initialValue: row.note || '', onSubmit: (value) => __awaiter(this, void 0, void 0, function* () {
                                let updatedDashboard = yield setDashboardNote(row.id, value);
                                updateDashboardEntryAfterPostApiCall(updatedDashboard);
                            }) }) }));
                },
            },
        },
        shouldShowXpm && {
            name: 'xpmJobs.list',
            label: 'Xpm Job',
            options: {
                display: ((_f = getDatatableColumnConfig('dashboardConfig', 'xpmJobs.list')) === null || _f === void 0 ? void 0 : _f.display) || false,
                download: false,
                filter: false,
                sort: false,
                customBodyRender: (_, tableMeta) => {
                    const jobs = tableMeta.rowData[0];
                    if (!jobs || jobs.length == 0) {
                        return '';
                    }
                    else if (jobs && jobs.length == 1) {
                        return _jsx(Link, { rel: "noopener noreferrer", target: "_blank", href: jobs[0].webUrl, children: _jsx(AutoEllipsis, { text: jobs[0].name, maxLength: 18 }) });
                    }
                    else {
                        return _jsx(StyledMuiLink, { onClick: (event) => {
                                setDialogContent({
                                    content: _jsx(JobsTable, { jobs: jobs }),
                                    title: 'Xpm Jobs',
                                    open: true,
                                    onClose: () => setDialogContent({ open: false }),
                                });
                                event.preventDefault();
                                event.stopPropagation();
                            }, children: "Multiple jobs found" });
                    }
                },
            },
        },
        shouldShowXpm && {
            name: 'xpmJobs.staff',
            label: 'Xpm Staff',
            options: Object.assign(Object.assign({ display: ((_g = getDatatableColumnConfig('dashboardConfig', 'xpmJobs.staff')) === null || _g === void 0 ? void 0 : _g.display) || false, download: false, filter: true, filterType: 'multiselect', filterOptions: {
                    names: [...new Set(dashboards.flatMap(dash => { var _a, _b; return ((_b = (_a = dash.xpmJobs) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.staff); }).map(staff => staff ? getFullName(staff) : 'None'))].sort(),
                    logic: (fullName, filters, row) => {
                        var _a;
                        const jobs = row[0];
                        const staff = (_a = jobs === null || jobs === void 0 ? void 0 : jobs[0]) === null || _a === void 0 ? void 0 : _a.staff;
                        const staffFullNames = (staff === null || staff === void 0 ? void 0 : staff.map((s => getFullName(s)))) || [];
                        if (!staffFullNames || staffFullNames.length == 0) {
                            return !filters.includes('None');
                        }
                        if (filters.length)
                            return !filters.some((filter) => staffFullNames.includes(filter));
                        return false;
                    },
                } }, customFilterWithPrefix('Xpm Staff')), { sort: true, customBodyRender(_, tableMeta) {
                    var _a;
                    const jobs = tableMeta.rowData[0];
                    const staff = (_a = jobs === null || jobs === void 0 ? void 0 : jobs[0]) === null || _a === void 0 ? void 0 : _a.staff;
                    const staffFullNames = (staff === null || staff === void 0 ? void 0 : staff.map((s => getFullName(s)))) || [];
                    return _jsx(_Fragment, { children: _jsx(DisplayWithSeeMoreChip, { nbRowsDisplayed: 1, separator: '\n', rows: staffFullNames }) });
                } }),
        },
        shouldShowXpm && {
            name: 'xpmJobs.manager',
            label: 'Xpm Manager',
            options: Object.assign(Object.assign({ display: ((_h = getDatatableColumnConfig('dashboardConfig', 'xpmJobs.manager')) === null || _h === void 0 ? void 0 : _h.display) || false, download: false, filter: true, filterType: 'multiselect', filterOptions: {
                    names: [...new Set(dashboards.map(dash => { var _a, _b; return (((_b = (_a = dash.xpmJobs) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.manager) ? getFullName(dash.xpmJobs[0].manager) : 'None'); }))].sort(),
                    logic: (fullName, filters) => {
                        if (!fullName) {
                            return !filters.includes('None');
                        }
                        if (filters.length)
                            return !filters.includes(fullName);
                        return false;
                    },
                } }, customFilterWithPrefix('Xpm Manager')), { sort: true, customBodyRender(value, tableMeta) {
                    var _a;
                    const jobs = tableMeta.rowData[0];
                    const manager = (_a = jobs === null || jobs === void 0 ? void 0 : jobs[0]) === null || _a === void 0 ? void 0 : _a.manager;
                    return manager ? getFullName(manager) : '';
                } }),
        },
        {
            name: 'status',
            label: 'Status',
            options: {
                // hint:  'Status:\n\n' +
                //   'Automatically moves to "ready to start" when the period is finished and no unreconciled bank transaction \n\n' +
                //   'Can be changed to complete by selecting the checkbox for that row and clicking on the mark as complete icon',
                filter: true,
                filterType: 'multiselect',
                filterOptions: {
                    names: Object.values(DashboardStatusEnum),
                    logic(status, filterVal) {
                        return !filterVal.includes(DashboardStatusEnum[status]);
                    },
                },
                sort: true,
                sortCompare: (order) => {
                    return (obj1, obj2) => {
                        let val1 = getStatusOrder(obj1.data);
                        let val2 = getStatusOrder(obj2.data);
                        return (val1 - val2) * (order === 'asc' ? 1 : -1);
                    };
                },
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender: (value) => {
                    const label = DashboardStatusEnum[value];
                    let color = 'default';
                    switch (value) {
                        case 'PENDING':
                            color = 'default';
                            break;
                        case 'READY_TO_START':
                            color = 'primary';
                            break;
                        case 'COMPLETE':
                            color = 'success';
                            break;
                        case 'NOT_REPORTING':
                            color = 'default';
                            break;
                    }
                    return _jsx(Chip, { label: label, color: color, variant: "outlined" });
                },
            },
        },
    ].filter(Boolean);
    const getHeaderSize = () => {
        if (messageContext.message) {
            return '248px';
        }
        else {
            return '220px';
        }
    };
    const options = {
        filterType: 'dropdown',
        enableNestedDataAccess: '.',
        rowsPerPageOptions: [25, 50, 100],
        rowsPerPage: getDatatableConfig('dashboardConfig', 'rowsPerPage') || 50,
        print: false,
        storageKey: 'dashboardConfig',
        tableBodyHeight: `calc(100vh - ${getHeaderSize()})`,
        fixedSelectColumn: true,
        fixedHeader: true,
        downloadOptions: {
            filename: `bas-ready-${yearMonth}.csv`,
        },
        setTableProps: () => {
            return {
                // material ui v5 only
                size: 'small',
                //border :'1'
            };
        },
        customToolbarSelect: function (selectedRows, _, setSelectedRows) {
            if (selectedRows.data.length > 0) {
                const selectedData = selectedRows.data.map(row => dashboards[row.dataIndex]);
                return (_jsxs("div", { children: [selectedData.filter(entry => entry.status === 'COMPLETE').length >= 1 && (_jsx(Tooltip, { title: "Mark as pending", children: _jsx(IconButton, { onClick: () => markAsPending(selectedData, setSelectedRows), children: _jsx(MarkAsPendingIcon, {}) }) })), selectedData.filter(entry => entry.status === 'COMPLETE').length !== selectedData.length && (_jsx(Tooltip, { title: "Mark as complete", children: _jsx(IconButton, { onClick: () => markAsComplete(selectedData, setSelectedRows), children: _jsx(MarkAsCompleteIcon, {}) }) })), user && (_jsx(Tooltip, { title: "Send reminder email(s)", children: _jsx(IconButton, { onClick: () => {
                                    messageContext.setMessage(empty());
                                    setEmailDialogContent({
                                        open: true,
                                        dashboardIds: selectedData.map(row => row.id),
                                        onClose: (result) => {
                                            if (result === null || result === void 0 ? void 0 : result.error) {
                                                messageContext.setMessage(error(result.error));
                                            }
                                            else if (result === null || result === void 0 ? void 0 : result.success) {
                                                refreshDataBackground();
                                                messageContext.setMessage(success(result.success));
                                            }
                                            setEmailDialogContent(undefined);
                                            setSelectedRows([]);
                                        },
                                    });
                                }, children: _jsx(MailIcon, {}) }) })), _jsx(Tooltip, { title: "Sync with xero", children: _jsx(IconButton, { onClick: () => syncWithXero(selectedData, setSelectedRows), children: _jsx(SyncIcon, {}) }) })] }));
            }
        },
    };
    return (_jsxs(ThemeProvider, { theme: getMuiTheme(), children: [_jsx(GenericDialog, Object.assign({}, dialogContent)), _jsx(EmailSendingDialog, { dashboardIds: emailDialogContent === null || emailDialogContent === void 0 ? void 0 : emailDialogContent.dashboardIds, open: (emailDialogContent === null || emailDialogContent === void 0 ? void 0 : emailDialogContent.open) || false, onClose: (emailDialogContent === null || emailDialogContent === void 0 ? void 0 : emailDialogContent.onClose) || (() => {
                }) }), _jsx(OrgSyncStatusDisplay, { syncReadyCallback: onSyncReady }), loading ? (_jsx(Loading, { message: loadingMessage })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }, children: [_jsx("div", {}), _jsx("div", { style: { display: 'flex', justifyContent: 'center', alignItems: 'center' }, children: _jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsx(DatePicker, { sx: {
                                            width: '185px',
                                            '.MuiInputBase-input': { padding: '10px' },
                                        }, value: dayjs(yearMonth, 'YYYY-MM'), format: "MMMM YYYY", minDate: dayjs('2020-01', 'YYYY-MM'), maxDate: dayjs(), disableFuture: true, views: ['month', 'year'], slotProps: {
                                            field: {
                                                readOnly: true,
                                            },
                                        }, onChange: newValue => {
                                            const newYearMonth = (newValue === null || newValue === void 0 ? void 0 : newValue.format('YYYY-MM')) || '';
                                            if (isValidYearMonth(newYearMonth)) {
                                                if (isPreviousYear(yearMonth, newYearMonth)) {
                                                    setYearMonth(newYearMonth.substring(0, 5) + '12');
                                                }
                                                else {
                                                    setYearMonth(newYearMonth);
                                                }
                                            }
                                        } }) }) }), _jsx("div", {})] }), dashboards.length === 0 ? (_jsx(NoOrganisation, {})) : (_jsx(MUIDataTable, { title: _jsx(SectionTitle, { children: "BAS Dashboard" }), data: dashboards || [], columns: columns, options: options }))] }))] }));
}
